import type {
  ResultUIStudentAcademicRisk,
  RiskCounters,
} from '@/domains/academicRisk';
import type { RiskIndicatorName } from '@/domains/shared';

/**
 * Use Case para agregar y contar indicadores de riesgo académico de múltiples estudiantes.
 *
 * Funcionalidad:
 * - Recorre todos los estudiantes en riesgo académico y cuenta las ocurrencias de cada tipo de riesgo
 * - Genera un objeto con contadores agrupados por tipo de riesgo
 * - Proporciona método helper para acceso seguro a contadores específicos
 *
 * Responsabilidades:
 * - Iterar sobre el array de riesgos de cada estudiante
 * - Acumular contadores de riesgo normalizados (lowercase)
 * - Proveer acceso seguro a contadores con valores por defecto
 * - Mantener estructura de contadores consistente
 */
export class AggregateAcademicRiskCountersUseCase {
  /**
   * Ejecuta la agregación de contadores de riesgo académico.
   *
   * @param students - Lista de estudiantes en riesgo académico con riskArray procesado
   */
  execute(students: ResultUIStudentAcademicRisk[]): RiskCounters {
    const counters: RiskCounters = {};

    for (const student of students) {
      for (const risk of student.riskArray) {
        const normalizedRisk = risk.toLowerCase();
        counters[normalizedRisk] = (counters[normalizedRisk] || 0) + 1;
      }
    }

    return counters;
  }

  /**
   * Obtiene el conteo para un tipo de riesgo específico con acceso seguro.
   *
   * @param counters - Objeto de contadores generado por execute()
   * @param riskType - Nombre del tipo de riesgo a consultar
   */
  getCountForRisk(counters: RiskCounters, riskType: RiskIndicatorName): number {
    return counters[riskType] || 0;
  }
}
